import CheckOutlined from '@mui/icons-material/CheckOutlined';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, { HTMLAttributes, useState } from 'react';

import { EditPartTypeLiveSearchProps } from './EditPartTypeLiveSearch.props';
import { ActionPermissions, LocationNodeData } from '../../../@types';
import { useSearchManufacturerNodesByClientAndContract } from '../../../queries/live-search-query';
import { LiveSearchBox, LiveSearchListItem } from '../../atoms/LiveSearchBox';
import { PermissionLink } from '../../atoms/PermissionLink';
import { LocationSearchItem } from '../../molecules/LocationSearchItem';

const containerSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  width: '100%',
  bgcolor: 'background.paper',
  border: 1,
  borderColor: 'v4.border',
  borderRadius: 1,
  px: 1.5,
  py: 1,
  minHeight: 56,
  boxSizing: 'border-box' as const,
};

const nameSx = {
  fontSize: '14px',
  fontWeight: 600,
  color: 'v4.accent',
  whiteSpace: 'nowrap' as const,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const subtitleSx = {
  fontSize: '12px',
  color: 'text.secondary',
  whiteSpace: 'nowrap' as const,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const iconButtonSx = {
  p: 0.5,
  color: 'text.secondary',
  '&:hover': { color: 'v4.accent' },
};

export const EditPartTypeLiveSearch: React.FC<EditPartTypeLiveSearchProps> = ({
  isEditable,
  partData,
  partTypeData,
  handlePartTypeChange,
}) => {
  const [isEdit, setIsEdit] = useState(false);
  const [partTypeId, setPartTypeId] = useState(0);

  const searchManufacturerNodesByClientAndContract =
    useSearchManufacturerNodesByClientAndContract();

  const openEditor = () => {
    // Always start the editor with no pending selection so a previous open's
    // value can't accidentally re-submit on a no-pick Save.
    setPartTypeId(0);
    setIsEdit(true);
  };

  const handleOnSubmit = () => {
    if (partTypeId === 0) {
      // Save without picking a new option is a no-op — close the editor without firing a change.
      setIsEdit(false);
      return;
    }
    handlePartTypeChange({ ...partData, partTypeId });
    setPartTypeId(0);
    setIsEdit(false);
  };

  const handleCancel = () => {
    setPartTypeId(0);
    setIsEdit(false);
  };

  if (isEdit) {
    return (
      <Box sx={containerSx}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <LiveSearchBox
            title="Part Type Search"
            timeOffset={400}
            value={partTypeData.name || ''}
            onClearValue={() => {
              setPartTypeId(0);
              return '';
            }}
            renderItem={(props: HTMLAttributes<HTMLLIElement>, option: LocationNodeData) => (
              <LiveSearchListItem {...props}>
                <LocationSearchItem data={option} />
              </LiveSearchListItem>
            )}
            onChange={(obj: LocationNodeData) => setPartTypeId(obj.id)}
            onApiInvoke={(name: string) =>
              searchManufacturerNodesByClientAndContract.mutateAsync({
                name,
                applicableNodeIds: partTypeData.applicableNodeIds,
              })
            }
          />
        </Box>
        <Tooltip title="Save part type" placement="top" arrow>
          <IconButton
            size="small"
            aria-label="Save part type"
            onClick={handleOnSubmit}
            sx={{ p: 0.5, color: 'success.main' }}
          >
            <CheckOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Cancel" placement="top" arrow>
          <IconButton
            size="small"
            aria-label="Cancel part type edit"
            onClick={handleCancel}
            sx={{ p: 0.5, color: 'error.main' }}
          >
            <CloseOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  const subtitleParts = [
    partTypeData.number ? `P/N: ${partTypeData.number}` : '',
    partTypeData.selectedParentHierarchy ? `Client: ${partTypeData.selectedParentHierarchy}` : '',
  ].filter(Boolean);

  return (
    <Box sx={containerSx}>
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <Typography sx={nameSx} title={partTypeData.name}>
          <PermissionLink
            permission={ActionPermissions.Configure_Part_Type_Search}
            href={`/configure/partTypes/${partTypeData.id}`}
            sx={{ color: 'v4.accent' }}
          >
            {partTypeData.name}
          </PermissionLink>
        </Typography>
        {subtitleParts.length > 0 && (
          <Typography sx={subtitleSx} title={subtitleParts.join(' · ')}>
            {subtitleParts.join(' · ')}
          </Typography>
        )}
      </Box>
      {isEditable && (
        <Tooltip title="Change part type" placement="top" arrow>
          <IconButton
            size="small"
            aria-label="Change part type"
            onClick={openEditor}
            sx={iconButtonSx}
          >
            <EditOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};
